import React, { useState, useEffect, useRef } from 'react';
import './AnalysisDashboard.css';
import AnalysisPieChart from './AnalysisPieChart';
import AnalysisBarChart from './AnalysisBarChart';
import ZeitstrahlWaBlock from './ZeitstrahlWaBlock';
import TimePill from './TimePill';

// WICHTIG: TileHeader MUSS ausserhalb von AnalysisDashboard definiert sein.
// Inner-Components werden bei jedem Render neu erzeugt -> React unmounted/remountet sie,
// wodurch Klicks (z.B. auf "Oeffnen") in Kacheln mit Hover-Re-Renders (Pie, Bar) verloren gehen koennen.
function TileHeader({ title, openLabel = 'Öffnen', onOpen, hint, extra }) {
  return (
    <div className="analysis-dashboard-tile-header">
      <div className="analysis-dashboard-tile-title">
        {title}
        {hint && <span className="analysis-dashboard-tile-hint" title={hint}> · {hint}</span>}
      </div>
      {extra && <div className="analysis-dashboard-tile-extra" onClick={(e) => e.stopPropagation()}>{extra}</div>}
      <button
        type="button"
        className="analysis-dashboard-open-btn"
        onClick={onOpen}
        title={`${openLabel}: vollstaendige Ansicht`}
      >{openLabel} →</button>
    </div>
  );
}

function loadTimeMode(key, fallback, allowed) {
  try {
    const v = localStorage.getItem(key);
    if (v && allowed.indexOf(v) >= 0) return v;
  } catch { /* ignore */ }
  return fallback;
}

/**
 * Auswertungs-Dashboard mit 5 Kacheln (Pie, BarChart, Zeitstrahl, Pivot, Statistik).
 * Klicks in den Kacheln schalten in den jeweiligen Tab um (setAnalysisViewMode)
 * und setzen optional einen Drill-Down (setPivotDrillDown / setEaDrillDown).
 */
function AnalysisDashboard({
  analysis,
  timeTypes,
  defaultTimeTypes,
  ablaufDbData,
  darkMode,
  decimalPlacesSollzeiten,
  formatTime,
  formatSollzeit,
  setAnalysisViewMode,
  setPivotDrillDown,
  setEaDrillDown,
  setStatistikGroupBy,
  computeStats,
  setAnalysisDrillOrigin,
}) {
  const [hoverM, setHoverM] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const palette = darkMode
    ? ['#60a5fa', '#fbbf24', '#34d399', '#f472b6', '#a78bfa', '#22d3ee', '#fb7185', '#facc15', '#4ade80', '#c084fc', '#38bdf8', '#fb923c', '#2dd4bf', '#e879f9']
    : ['#3b82f6', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444', '#eab308', '#22c55e', '#a855f7', '#0ea5e9', '#f97316', '#14b8a6', '#d946ef'];
  const deterministicColor = (key) => {
    if (!key) return palette[0];
    let h = 0;
    for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
    return palette[Math.abs(h) % palette.length];
  };
  const colorForTimeType = (name) => {
    const fromConfig = (Array.isArray(timeTypes) ? timeTypes.find(tt => tt.name === name) : null)?.color;
    const fromDefaults = (Array.isArray(defaultTimeTypes) ? defaultTimeTypes.find(tt => tt.name === name) : null)?.color;
    return fromConfig || fromDefaults || (name === 'Keine Zeitart' ? '#94a3b8' : null) || deterministicColor(String(name || ''));
  };
  const colorForKuerzel = (name) => {
    if (name === 'Kein Kürzel') return darkMode ? '#6b7280' : '#9ca3af';
    return deterministicColor(String(name || ''));
  };

  // Sollzeit-Toggle-Modi je Kachel; persistiert via localStorage.
  const [pieTimeMode, setPieTimeMode] = useState(() => loadTimeMode('dashboardPieTimeMode', 'ist', ['ist', 'soll']));
  const [barTimeMode, setBarTimeMode] = useState(() => loadTimeMode('dashboardBarTimeMode', 'vergleich', ['ist', 'soll', 'vergleich']));
  useEffect(() => { try { localStorage.setItem('dashboardPieTimeMode', pieTimeMode); } catch { /* ignore */ } }, [pieTimeMode]);
  useEffect(() => { try { localStorage.setItem('dashboardBarTimeMode', barTimeMode); } catch { /* ignore */ } }, [barTimeMode]);

  const byTimeType = analysis.byTimeType || {};
  const byKuerzel = analysis.byKuerzel || {};

  const timeTypeKeys = Object.keys(byTimeType).sort((a, b) => {
    if (a === 'Keine Zeitart') return 1;
    if (b === 'Keine Zeitart') return -1;
    return a.localeCompare(b);
  });
  const fmtSoll = (v) => (formatSollzeit ? formatSollzeit(v, decimalPlacesSollzeiten) + ' min' : `${v}`);
  const pieValueOf = (d) => pieTimeMode === 'soll' ? (d.totalVorgabeTime || 0) : (d.totalTime || 0);
  const pieSlices = timeTypeKeys.map(name => {
    const d = byTimeType[name] || {};
    return { key: name, label: name, value: pieValueOf(d), count: d.count || 0, color: colorForTimeType(name) };
  });
  const pieGrand = pieSlices.reduce((s, x) => s + (x.value || 0), 0);
  const pieFormatValue = pieTimeMode === 'soll' ? fmtSoll : formatTime;
  const pieCenterLabel = pieTimeMode === 'soll' ? 'Summe SOLL' : 'Summe IST';

  const kuerzelKeys = Object.keys(byKuerzel);
  const baseBarRows = kuerzelKeys.map(name => {
    const d = byKuerzel[name] || {};
    return {
      key: name,
      label: name,
      ist: d.totalTime || 0,
      soll: d.totalVorgabeTime || 0,
      count: d.count || 0,
      color: colorForKuerzel(name),
    };
  });
  // Modus 'ist' und 'soll': nur eine Reihe pro Zeile, andere Beschriftung. 'vergleich': IST + SOLL gepaart.
  const barRows = baseBarRows.map(r => {
    if (barTimeMode === 'soll') return { ...r, ist: r.soll, soll: 0 };
    if (barTimeMode === 'ist') return { ...r, soll: 0 };
    return r;
  });
  const barShowSoll = barTimeMode === 'vergleich';
  const barFormatIst = barTimeMode === 'soll' ? fmtSoll : formatTime;
  const barSortBy = 'ist';

  const flatMeasurements = Object.values(byTimeType).flatMap(d => d.measurements || []);
  const wagrouped = {};
  flatMeasurements.forEach(m => {
    const key = m.resumeNumber ?? '—';
    if (!wagrouped[key]) wagrouped[key] = [];
    wagrouped[key].push(m);
  });
  const waOrder = Object.keys(wagrouped).sort((a, b) => {
    if (a === '—') return 1;
    if (b === '—') return -1;
    const na = Number(a);
    const nb = Number(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
  const waPreview = waOrder.slice(0, 6);
  const waMore = waOrder.length - waPreview.length;
  const toT = (x) => {
    const v = x?.timestamp ? new Date(x.timestamp).getTime() : 0;
    return (typeof v === 'number' && !isNaN(v)) ? v : 0;
  };
  const getColor = (m) => {
    const key = (m.timeType == null || m.timeType === '') ? 'Keine Zeitart' : m.timeType;
    return colorForTimeType(key);
  };
  const handleSegmentEnter = (e, m) => {
    clearTimeout(hoverTimeoutRef.current);
    setHoverM({ m, rect: e.currentTarget.getBoundingClientRect() });
  };
  const handleSegmentLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoverM(null), 180);
  };

  const pivotActivities = Object.keys(analysis.pivot || {}).sort();
  const allPivotTimeTypes = [...new Set(Object.values(analysis.pivot || {}).flatMap(a => Object.keys(a)))]
    .filter(tt => tt !== 'x__')
    .sort();
  const activitySumTime = (a) => {
    const cells = analysis.pivot[a] || {};
    return Object.entries(cells).filter(([tt]) => tt !== 'x__').reduce((s, [, c]) => s + (c?.totalTime || 0), 0);
  };
  const topActivities = [...pivotActivities].sort((a, b) => activitySumTime(b) - activitySumTime(a)).slice(0, 5);

  const kuerzelStatsRows = kuerzelKeys.map(k => {
    const data = byKuerzel[k];
    const times = (data?.measurements || []).map(m => m.individualTime).filter(v => v != null);
    const stats = computeStats ? computeStats(times) : {};
    return {
      key: k,
      n: stats.n || data.count || 0,
      mean: stats.mean,
      min: stats.min,
      max: stats.max,
      total: data.totalTime || 0,
    };
  }).sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5);

  const goTab = (mode, drill, ea) => {
    // Origin merken: 'dashboard' wenn ein Drilldown geoeffnet wird, 'tab' wenn nur der Tab gewechselt wird.
    const opensDrilldown = (drill !== undefined && drill !== null) || (ea !== undefined && ea !== null);
    if (setAnalysisDrillOrigin) setAnalysisDrillOrigin(opensDrilldown ? 'dashboard' : 'tab');
    if (drill !== undefined) setPivotDrillDown(drill);
    if (ea !== undefined) setEaDrillDown(ea);
    setAnalysisViewMode(mode);
  };

  return (
    <div className={`analysis-dashboard ${darkMode ? 'dark-mode' : ''}`}>
      {/* Kachel 1: Bilanz Zeitarten als Pie */}
      <div className="analysis-dashboard-tile">
        <TileHeader
          title="Bilanz Zeitarten"
          onOpen={() => goTab('timeType', null)}
          hint={pieSlices.length > 0 ? `${pieSlices.length} Zeitart(en), Summe ${pieFormatValue(pieGrand)}` : undefined}
          extra={
            <TimePill
              value={pieTimeMode}
              onChange={setPieTimeMode}
              darkMode={darkMode}
              options={[
                { id: 'ist', label: 'IST', title: 'Anteile an der IST-Gesamtzeit' },
                { id: 'soll', label: 'SOLL', title: 'Anteile an der Vorgabezeit (SOLL)' },
              ]}
            />
          }
        />
        <div className="analysis-dashboard-tile-body">
          <AnalysisPieChart
            slices={pieSlices}
            formatValue={pieFormatValue}
            centerValue={pieFormatValue(pieGrand)}
            centerLabel={pieCenterLabel}
            darkMode={darkMode}
            size={200}
            ariaLabel="Bilanz Zeitarten Kreisdiagramm"
            onSliceClick={(key) => {
              const data = byTimeType[key];
              const measurements = data?.measurements;
              goTab('timeType', measurements && measurements.length ? { timeType: key, measurements } : null);
            }}
          />
        </div>
      </div>

      {/* Kachel 2: Bilanz Kuerzel als Bar (IST / SOLL / Vergleich) */}
      <div className="analysis-dashboard-tile">
        <TileHeader
          title="Bilanz Kürzel"
          onOpen={() => goTab('kuerzel', null)}
          hint={baseBarRows.length > 0 ? `Top 8 von ${baseBarRows.length}` : undefined}
          extra={
            <TimePill
              value={barTimeMode}
              onChange={setBarTimeMode}
              darkMode={darkMode}
              options={[
                { id: 'ist', label: 'IST', title: 'Nur IST-Gesamtzeit' },
                { id: 'soll', label: 'SOLL', title: 'Nur Vorgabezeit (SOLL)' },
                { id: 'vergleich', label: 'Vergleich', title: 'IST und SOLL gepaart' },
              ]}
            />
          }
        />
        <div className="analysis-dashboard-tile-body">
          <AnalysisBarChart
            rows={barRows}
            formatIst={barFormatIst}
            formatSoll={fmtSoll}
            darkMode={darkMode}
            maxRows={8}
            sortBy={barSortBy}
            showSoll={barShowSoll}
            onRowClick={(key) => {
              const data = byKuerzel[key];
              const measurements = data?.measurements;
              goTab('kuerzel', measurements && measurements.length ? { kuerzel: key, measurements } : null);
            }}
          />
        </div>
      </div>

      {/* Kachel 3: Zeitstrahl (breit) */}
      <div className="analysis-dashboard-tile analysis-dashboard-tile-wide">
        <TileHeader
          title="Zeitstrahl"
          onOpen={() => goTab('zeitstrahl', null)}
          hint={waOrder.length > 0 ? `${waPreview.length} von ${waOrder.length} WA aufgeklappt` : undefined}
        />
        <div className="analysis-dashboard-tile-body">
          {waOrder.length === 0 ? (
            <div className="analysis-dashboard-empty">Keine Messdaten (ohne x__) fuer den Zeitstrahl.</div>
          ) : (
            <div className="compact-zeitstrahl analysis-dashboard-zeitstrahl">
              {waPreview.map(waKey => {
                const list = [...(wagrouped[waKey] || [])].sort((a, b) => toT(a) - toT(b));
                return (
                  <ZeitstrahlWaBlock
                    key={waKey}
                    waKey={waKey}
                    list={list}
                    expanded={true}
                    onToggle={() => goTab('zeitstrahl', null)}
                    getColor={getColor}
                    formatTime={formatTime}
                    onSegmentEnter={handleSegmentEnter}
                    onSegmentLeave={handleSegmentLeave}
                    compact
                  />
                );
              })}
              {waMore > 0 && (
                <button
                  type="button"
                  className="analysis-dashboard-zeitstrahl-more"
                  onClick={() => goTab('zeitstrahl', null)}
                >+{waMore} weitere WAs anzeigen →</button>
              )}
            </div>
          )}
        </div>
        {hoverM && (() => {
          const { m, rect } = hoverM;
          const descZeitart = (timeTypes.find(tt => tt.name === m.timeType) || defaultTimeTypes.find(d => d.name === m.timeType))?.description;
          const kuerzelMatch = (ablaufDbData && Array.isArray(ablaufDbData)) ? ablaufDbData.find(k => k.kuerzel === m.kuerzel) : null;
          const descKuerzel = kuerzelMatch ? (kuerzelMatch.beschreibung || kuerzelMatch.fullText || '') : '';
          let left = rect.left;
          let top = rect.bottom + 6;
          const w = 320;
          if (left + w > window.innerWidth) left = window.innerWidth - w - 8;
          if (left < 8) left = 8;
          if (top + 240 > window.innerHeight - 8 && rect.top > 260) top = rect.top - 244;
          return (
            <div
              className={`zeitstrahl-popover ${darkMode ? 'zeitstrahl-popover-dark' : ''}`}
              style={{ position: 'fixed', left, top, maxWidth: w, zIndex: 10000 }}
              onMouseEnter={() => clearTimeout(hoverTimeoutRef.current)}
              onMouseLeave={() => setHoverM(null)}
            >
              <div className="zeitstrahl-popover-header">
                #{m.number} {m.kuerzel || m.activity || '—'} · {formatTime(m.individualTime || 0)} · {m.timeType || 'Keine Zeitart'}
              </div>
              {descZeitart && <div className="zeitstrahl-popover-line"><strong>Zeitart:</strong> {descZeitart}</div>}
              {descKuerzel && <div className="zeitstrahl-popover-line"><strong>Tätigkeit:</strong> {descKuerzel}</div>}
              {m.note && <div className="zeitstrahl-popover-line"><strong>Notiz:</strong> {m.note}</div>}
            </div>
          );
        })()}
      </div>

      {/* Kachel 4: Pivot kompakt */}
      <div className="analysis-dashboard-tile">
        <TileHeader
          title="Pivot"
          onOpen={() => goTab('pivot', null)}
          hint={pivotActivities.length > 0 ? `Top 5 von ${pivotActivities.length} Tätigkeiten` : undefined}
        />
        <div className="analysis-dashboard-tile-body">
          {topActivities.length === 0 || allPivotTimeTypes.length === 0 ? (
            <div className="analysis-dashboard-empty">Keine Pivot-Daten.</div>
          ) : (
            <div className="analysis-dashboard-pivot-wrap">
              <table className="analysis-dashboard-pivot">
                <thead>
                  <tr>
                    <th>Tätigkeit</th>
                    {allPivotTimeTypes.slice(0, 5).map(tt => (<th key={tt}>{tt}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {topActivities.map(a => (
                    <tr key={a}>
                      <td className="analysis-dashboard-pivot-row-label" title={a}>{a.length > 18 ? a.slice(0, 16) + '…' : a}</td>
                      {allPivotTimeTypes.slice(0, 5).map(tt => {
                        const cell = (analysis.pivot[a] || {})[tt];
                        const has = cell && cell.count > 0;
                        return (
                          <td
                            key={tt}
                            className={has ? 'has-data' : 'no-data'}
                            onClick={() => {
                              if (has) goTab('pivot', { activity: a, timeType: tt, measurements: cell.measurements });
                            }}
                            title={has ? `${cell.count} Messpunkt(e), ${formatTime(cell.totalTime)}` : 'Keine Daten'}
                          >
                            {has ? <span><strong>{cell.count}</strong><br /><span className="analysis-dashboard-pivot-time">{formatTime(cell.totalTime)}</span></span> : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Kachel 5: Statistik (Top-Kennzahlen) */}
      <div className="analysis-dashboard-tile">
        <TileHeader
          title="Statistik"
          onOpen={() => { if (setStatistikGroupBy) setStatistikGroupBy('kuerzel'); goTab('statistik', null); }}
          hint={kuerzelStatsRows.length > 0 ? `Top 5 Kürzel nach IST` : undefined}
        />
        <div className="analysis-dashboard-tile-body">
          {kuerzelStatsRows.length === 0 ? (
            <div className="analysis-dashboard-empty">Keine Statistik-Daten.</div>
          ) : (
            <table className="analysis-dashboard-stats">
              <thead>
                <tr>
                  <th>Kürzel</th>
                  <th>n</th>
                  <th>Ø</th>
                  <th>Min</th>
                  <th>Max</th>
                </tr>
              </thead>
              <tbody>
                {kuerzelStatsRows.map(r => (
                  <tr
                    key={r.key}
                    className="analysis-dashboard-stats-row"
                    onClick={() => {
                      if (setStatistikGroupBy) setStatistikGroupBy('kuerzel');
                      const data = byKuerzel[r.key];
                      const egKeys = [...new Set((data?.measurements || []).flatMap(m => (m.einflussgroessen && typeof m.einflussgroessen === 'object') ? Object.keys(m.einflussgroessen) : []))];
                      goTab('statistik', null, { key: r.key, kuerzel: r.key, egList: egKeys, selectedEG: egKeys[0] || null });
                    }}
                    title={`Klicken: Statistik fuer ${r.key} oeffnen`}
                  >
                    <td title={r.key}>{r.key.length > 14 ? r.key.slice(0, 12) + '…' : r.key}</td>
                    <td>{r.n}</td>
                    <td>{r.mean != null ? formatTime(r.mean) : '–'}</td>
                    <td>{r.min != null ? formatTime(r.min) : '–'}</td>
                    <td>{r.max != null ? formatTime(r.max) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalysisDashboard;
